/**
 * The server-resolved platform → persona → user arrangement for a record's
 * related types, plus the caller's own provenance (#1466 B4).
 *
 * ── What was measured, and why a count could not be the answer ─────────────
 * A record page decides which related types to show, and in what order, from
 * `relationship_type` / `relationship_type_pair`. `relations.ts:282-284` sorts
 * them `isIdentity ? 2 : kind === 'list' ? 0 : 1`, and the only per-user signal
 * that has ever existed is `relationship_frequency` — a COUNT, bumped on every
 * deliberate pick, applied by `orderSlots` (`useSlotFrequency.ts`).
 *
 * A count is not an order. It cannot say *pin this*, it cannot say *hide that*,
 * and it cannot say *this one first even though I rarely open it*. It is a
 * LEARNED signal, and the thing it structurally cannot express is a STATED one.
 *
 * So this is the resolved stated rung. `orderSlots` gains a stated arm ABOVE frequency —
 * `stated → populated → frequency → alpha` — and frequency stays as the answer
 * for every slot the statement does not mention. The learned signal is not
 * deleted, it is demoted: a person who has never opened the editor sees exactly
 * what they saw before, to the row.
 *
 * ── Where precedence lives ──────────────────────────────────────────────────
 * Core 0.297 owns the cascade: deployment/persona documents live in
 * `core.experience(kind='related')`; a person's document lives in
 * `core.user_setting`, key `related_order`, shaped:
 *
 *   { "<from_entity>@<host>": { "groups": [ … ] } }
 *
 * `core.related_order` resolves it once; this hook does not reproduce that
 * precedence. `core.my_related_order` answers provenance separately so Reset
 * removes only the caller's host-scoped statement and reveals the rung below.
 * Experience rows travel through ALM; user settings do not.
 *
 * ── Why the NAVIGATION document grammar, verbatim ──────────────────────────
 * Reorder-and-hide over a list of named things is a grammar this repo already
 * owns and has already paid to get right: `packages/ui/src/shell/
 * navigationDocument.ts` carries `rowsFor` (base is the authority on
 * EXISTENCE), `reconcile` (a document written before a module was installed
 * appends what it never heard of, rather than hiding it forever) and
 * `documentFrom`. A second reorder/hide vocabulary would be `ui-and-experience.md`
 * §1's failure mode with a different noun.
 *
 * So a related type is a nav ITEM whose `to` is the SLOT ID
 * (`works_at:in:core_person`), and the base document is one group built from
 * `listSlots(schema, entity)` — the surface being edited supplies the base,
 * always (owner ruling M3). Three properties fall out of that with no code:
 *
 *   · a slot the schema no longer registers disappears from the editor, because
 *     `rowsFor` drops any `to` the base does not have;
 *   · a relationship type installed after the document was written is APPENDED
 *     rather than silently absent, because `reconcile` treats "not mentioned"
 *     as new;
 *   · a HIDDEN type is still in the document, so it is still in the editor, so
 *     it can be switched back on. Hidden is carried, never deleted (O5).
 *
 * ── Two directions of one relationship are two statements ──────────────────
 * The key is the SLOT id, not the relationship key, for the reason
 * `useSlotFrequency.ts:17-21` already gives: a user who lives in "People of this
 * organization" has said nothing about "Organizations this person works at".
 */
import { useCallback, useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { isNavDocument, type NavDocument } from '@solum/ui'
import { useSolum } from '../data/client'
import { CONSOLE_HOST } from '../data/queries'
import { orderSlots } from './useSlotFrequency'

/**
 * The one group a base document has.
 *
 * A base group's label is the platform's word for itself and cannot be renamed
 * (`navigationDocument.ts` — a document carrying a renamed base label
 * round-trips back to it), so this string is what a person sees above their
 * related types until they invent a section of their own.
 */
export const RELATED_GROUP = 'Related'

/** What a surface hands the editor: the slots it would show with no statement. */
export function relatedBase(slots: { id: string; label: string }[]): NavDocument {
  return { groups: [{ label: RELATED_GROUP, items: slots.map((s) => ({ label: s.label, to: s.id })) }] }
}

/** The stated rung, resolved into the three questions a surface asks of it. */
export interface RelatedRung {
  /**
   * The position this person stated for that slot, or `null` when they stated
   * nothing about it.
   *
   * `null` is not "last" — it is *unmentioned*, and `orderSlots` answers an
   * unmentioned slot with the learned order. Collapsing the two would delete
   * the frequency signal the moment anybody dragged one row.
   */
  statedOf: (slotId: string) => number | null
  /** O5 — a person may switch a related type off. It stays in the document. */
  isHidden: (slotId: string) => boolean
  /** Which of the person's sections a slot sits in; the base group by default. */
  groupOf: (slotId: string) => string
  /** Section order as the document states it — for a surface that draws headings. */
  groupOrder: string[]
}

/** The rung a person who has stated nothing has: silent on every question. */
export const NO_RUNG: RelatedRung = {
  statedOf: () => null,
  isHidden: () => false,
  groupOf: () => RELATED_GROUP,
  groupOrder: [RELATED_GROUP],
}

/**
 * Read a stored document as a rung.
 *
 * Deliberately reads the RAW document rather than `reconcile(doc, base)`: the
 * question here is *what did this person say*, and reconcile's whole job is to
 * append what they never heard of. Running it first would hand every newly
 * installed relationship type a stated position nobody stated, which is the
 * "frequency silently replaced" failure in one line.
 *
 * Positions run across the whole document, sections included, so a section is a
 * grouping AND a coarse ordering — which is what it looks like in the editor.
 * First mention wins, matching `rowsFor`: a hand-written document naming one
 * slot twice is two places to hide the same thing from, not two slots.
 */
export function rungFrom(doc: NavDocument | null | undefined): RelatedRung {
  if (!isNavDocument(doc)) return NO_RUNG
  const stated = new Map<string, number>()
  const hidden = new Set<string>()
  const group = new Map<string, string>()
  const order: string[] = []
  let n = 0
  for (const g of doc.groups) {
    if (!g || !order.includes(g.label)) order.push(g?.label ?? RELATED_GROUP)
    for (const i of g?.items ?? []) {
      if (!i || stated.has(i.to)) continue
      stated.set(i.to, n++)
      group.set(i.to, g.label)
      // A hidden GROUP hides everything under it — `visibleNav` drops the group
      // wholesale, and a rung that disagreed would show the rows of a section
      // the person switched off.
      if (i.hidden || g.hidden) hidden.add(i.to)
    }
  }
  if (!order.includes(RELATED_GROUP)) order.push(RELATED_GROUP)
  return {
    statedOf: (id) => stated.get(id) ?? null,
    isHidden: (id) => hidden.has(id),
    groupOf: (id) => group.get(id) ?? RELATED_GROUP,
    groupOrder: order,
  }
}

/** A section of the resolved order — a heading and the slots under it. */
export interface RelatedGroup<T> { label: string; slots: T[] }

/**
 * The rung applied: hidden types dropped, the rest grouped and ordered.
 *
 * Grouping happens BEFORE ordering because the DOCUMENT's section order is the
 * authority on where a heading goes — `groupOrder` seeds the buckets, so a
 * person who put their own section above the platform's gets it above, whatever
 * order the schema happens to hand the slots in. That one IS load-bearing:
 * dropping the seeding reds *sections come out in the DOCUMENT order*.
 *
 * ── A green mutation, recorded rather than dropped ─────────────────────────
 * The obvious alternative — order flat, then bucket by first appearance — was
 * applied as a mutation and the suite stayed GREEN (16/16). It is genuinely
 * equivalent today, for two reasons that are coincidences of the current rules
 * rather than properties anybody argued: a stated slot always outranks an
 * unmentioned one (so no unmentioned slot can ever land between two mentioned
 * ones and split a section), and a `Map` keyed by label cannot hold one heading
 * twice however often it is pushed to. Change either — give the unmentioned a
 * rank, or key sections by `id` so two may share a name — and the two stop
 * agreeing. This order is kept because it does not depend on either.
 */
export function groupSlots<T extends { id: string; label: string }>(
  slots: T[],
  rung: RelatedRung,
  countOf: (id: string) => number | null,
  frequencyOf: (id: string) => number,
  declaredOf: (id: string) => number | null,
): RelatedGroup<T>[] {
  const visible = slots.filter((s) => !rung.isHidden(s.id))
  const by = new Map<string, T[]>()
  for (const label of rung.groupOrder) by.set(label, [])
  for (const s of visible) {
    const label = rung.groupOf(s.id)
    const bucket = by.get(label) ?? []
    if (!by.has(label)) by.set(label, bucket)
    bucket.push(s)
  }
  return [...by.entries()]
    .map(([label, xs]) => ({ label, slots: orderSlots(xs, countOf, frequencyOf, rung.statedOf, declaredOf) }))
    .filter((g) => g.slots.length > 0)
}

/** The same answer as one list — what a picker wants, and what a picker got before. */
export function applyRung<T extends { id: string; label: string }>(
  slots: T[],
  rung: RelatedRung,
  countOf: (id: string) => number | null,
  frequencyOf: (id: string) => number,
  declaredOf: (id: string) => number | null,
): T[] {
  return groupSlots(slots, rung, countOf, frequencyOf, declaredOf).flatMap((g) => g.slots)
}

export interface RelatedOrder {
  /** The server-resolved arrangement, or null when no rung has spoken. */
  document: NavDocument | null
  /** The caller's own statement, separate from the resolved answer. */
  ownDocument: NavDocument | null
  /** True when the caller has a stored statement rather than inheriting. */
  isCustomised: boolean
  rung: RelatedRung
  /** `null` clears the caller's statement and reveals persona/platform inheritance. */
  save: (doc: NavDocument | null, done?: { onSuccess?: () => void; onError?: (e: Error) => void }) => void
  busy: boolean
}

export function useRelatedOrder(entity: string, host = CONSOLE_HOST): RelatedOrder {
  const { client, isLive } = useSolum()
  const qc = useQueryClient()

  /* Resolution belongs to core. Rebuilding platform/persona/user precedence in
     React would make every consumer a second authority. */
  const resolved = useQuery({
    queryKey: ['related-order', entity, host],
    queryFn: () => client.raw().rpcScalar<NavDocument | null>('related_order', {
      p_entity: entity, p_host: host,
    }, 'core'),
    enabled: isLive,
    staleTime: 60_000,
  })

  /* Provenance is separate because the resolved grammar intentionally does not
     disclose which rung won; this answer drives “customised” and Reset. */
  const own = useQuery({
    queryKey: ['related-order-pin', entity, host],
    queryFn: () => client.raw().rpcScalar<NavDocument | null>('my_related_order', {
      p_entity: entity, p_host: host,
    }, 'core'),
    enabled: isLive,
    staleTime: 60_000,
  })

  const document = isNavDocument(resolved.data) ? resolved.data : null
  const ownDocument = isNavDocument(own.data) ? own.data : null

  const save = useMutation({
    mutationFn: (next: NavDocument | null) => client.raw().rpcScalar('pin_related_order', {
      p_entity: entity, p_document: next, p_host: host,
    }, 'core'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['related-order', entity, host] })
      void qc.invalidateQueries({ queryKey: ['related-order-pin', entity, host] })
    },
  })

  return {
    document,
    ownDocument,
    isCustomised: ownDocument !== null,
    rung: useMemo(() => rungFrom(document), [document]),
    save: useCallback((doc, done) => save.mutate(doc, {
      onSuccess: () => done?.onSuccess?.(),
      onError: (e) => done?.onError?.(e as Error),
    }), [save]),
    busy: save.isPending,
  }
}

/** An exact administrator-authored rung. Unlike `related_order`, this does not
 * resolve the current user's own preference over the shared document: it is
 * the provenance needed to label Reset honestly and delete only this target. */
export interface RelatedOrderDefault {
  key: string
  entity_type: string
  persona: string | null
  host: string | null
  document: NavDocument
  scope: 'tenant' | 'persona' | 'solution' | 'system' | null
  version: number
  updated_at: string
}

const DEFAULTS_KEY = ['related-order-defaults'] as const

export function useRelatedOrderDefaults(enabled: boolean) {
  const { client } = useSolum()
  return useQuery<RelatedOrderDefault[]>({
    queryKey: DEFAULTS_KEY,
    enabled,
    retry: false,
    queryFn: () => client.raw().rpcTable<RelatedOrderDefault>('related_order_defaults', {}, 'core'),
  })
}

export function useSetRelatedOrderDefault() {
  const { client } = useSolum()
  const qc = useQueryClient()
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: (value: { entity: string; persona: string | null; host: string; document: NavDocument }) =>
      client.raw().rpcScalar('set_related_order', {
        p_persona: value.persona,
        p_entity: value.entity,
        p_document: value.document,
        p_host: value.host,
      }, 'core'),
    onSuccess: (_data, value) => {
      void qc.invalidateQueries({ queryKey: DEFAULTS_KEY })
      void qc.invalidateQueries({ queryKey: ['related-order', value.entity, value.host] })
    },
  })
}

export function useClearRelatedOrderDefault() {
  const { client } = useSolum()
  const qc = useQueryClient()
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: (value: { entity: string; persona: string | null; host: string }) =>
      client.raw().rpcScalar<boolean>('clear_related_order', {
        p_persona: value.persona,
        p_entity: value.entity,
        p_host: value.host,
      }, 'core'),
    onSuccess: (_data, value) => {
      void qc.invalidateQueries({ queryKey: DEFAULTS_KEY })
      void qc.invalidateQueries({ queryKey: ['related-order', value.entity, value.host] })
    },
  })
}
